import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDepartments } from "../../../redux/actions";
import ColorBullet from "../../ColorBullet";
import TicketsSearch from "../tickets/TicketsSearch";
import EmbedListQuickFilters, {
    buildDepartmentQuickFilterOptions,
    buildTrustedDomainQuickFilterOptions,
} from "./embedList/EmbedListQuickFilters";
import EmbedDepartmentLabelHint from "./EmbedDepartmentLabelHint";
import EmbedListActiveQuickFilter, {
    getEmbedListActiveFilterState,
} from "./embedList/EmbedListActiveQuickFilter";
import EmbedListLayout from "./embedList/EmbedListLayout";
import EmbedListFilter, { EMBED_FORMS_ADVANCED_FILTER_OPTIONS } from "./embedList/EmbedListFilter";
import EmbedListSortableTh from "./embedList/EmbedListSortableTh";
import {
    buildEmbedListApiParams,
    patchEmbedListRowActive,
} from "./embedList/embedListUtils";
import {
    Button,
    Col,
    Container,
    Dropdown,
    Form,
    Modal,
    OverlayTrigger,
    Pagination,
    Row,
    Table,
    Tooltip,
    Placeholder,
} from "react-bootstrap";
import axios from "axios";
import Cookies from "js-cookie";
import {
    BsClipboard,
    BsExclamationTriangleFill,
    BsThreeDotsVertical,
} from "react-icons/bs";
import { Link, useHistory } from "react-router-dom";
import plus from "../../../../css/img/plus.svg";
import { baseURL } from "../../../helpers/baseURL";
import MainContainer from "../../MainContainer";
import { useEmbedFormDialogs } from "./useEmbedFormDialogs";
import tableStyles from "../tickets/TicketsTable.module.css";
import EmbedFormEditorPreview from "./EmbedFormEditorPreview";
import EmbedHowToModal from "./EmbedHowToModal";
import "./embed-form-editor.css";
import { EmbedFormBtn } from "./embedFormButtons";
import { EmbedFormsActiveSelect } from "./embedFormsActiveSelect";
import { EMBED_FORMS_ACTIONS_MENU_POPPER } from "./embedFormsActionsMenu";
import { handleEmbedAuthRedirect } from "./embedAuthRedirect";

const authHeaders = () => ({
    Authorization: `Bearer ${Cookies.get("access_token")}`,
});

const EMBED_FORM_NAME_DISPLAY_MAX = 48;

function formatEmbedFormName(name) {
    const text = String(name ?? "").trim();
    if (text.length <= EMBED_FORM_NAME_DISPLAY_MAX) {
        return { display: text || "—", full: text, truncated: false };
    }
    return {
        display: `${text.slice(0, EMBED_FORM_NAME_DISPLAY_MAX).trimEnd()}...`,
        full: text,
        truncated: true,
    };
}

const DEFAULT_FORM_COLUMNS = {
    name: { visible: true, label: "Name" },
    created_at: { visible: true, label: "Created" },
    is_active: { visible: true, label: "Active" },
    department: { visible: true, label: "Department" },
    trusted_domains: { visible: true, label: "Trusted domains" },
};

const DEFAULT_FORM_FILTERS = {
    text: "",
    department: [],
    is_active: [],
    trusted_domain: [],
    created_at: "",
    created_at_range: [],
    display_mode: [],
};

const EmbedTicketForms = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { confirmDialog, alertDialog, dialogNodes } = useEmbedFormDialogs();
    const departments = useSelector((state) => state.departmentsReducer?.departments) ?? [];
    const departmentsLoading = useSelector((state) => state.departmentsReducer?.loading);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [trustedDomains, setTrustedDomains] = useState([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [filters, setFilters] = useState(DEFAULT_FORM_FILTERS);
    const [sort, setSort] = useState({ by: "id", direction: "desc" });
    const [columns, setColumns] = useState(() => {
        try {
            const saved = localStorage.getItem("embedFormsTableColumns");
            return saved ? JSON.parse(saved) : DEFAULT_FORM_COLUMNS;
        } catch {
            return DEFAULT_FORM_COLUMNS;
        }
    });
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 20,
        current_page: 1,
        last_page: 1,
    });
    const [preview, setPreview] = useState({ show: false, cfg: null });
    const [embedHowTo, setEmbedHowTo] = useState({
        show: false,
        uuid: null,
        formName: "",
        displayMode: "inline",
    });
    const [updatingActiveId, setUpdatingActiveId] = useState(null);

    const load = async (pageNum = page, pageSize = perPage, filtersSnapshot = filters, sortSnapshot = sort) => {
        setLoading(true);
        try {
            const params = buildEmbedListApiParams(filtersSnapshot, sortSnapshot, {
                page: pageNum,
                perPage: pageSize,
            });
            const { data } = await axios.get(`${baseURL}ticket-embed-forms`, {
                headers: authHeaders(),
                params,
            });
            if (Array.isArray(data)) {
                setRows(data);
                setPagination({
                    total: data.length,
                    per_page: data.length,
                    current_page: 1,
                    last_page: 1,
                });
            } else {
                setRows(Array.isArray(data?.data) ? data.data : []);
                setPagination({
                    total: data?.total ?? 0,
                    per_page: data?.per_page ?? 20,
                    current_page: data?.current_page ?? 1,
                    last_page: data?.last_page ?? 1,
                });
            }
        } catch (e) {
            if (handleEmbedAuthRedirect(e)) {
                return;
            }
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!departments?.length && !departmentsLoading) {
            dispatch(fetchDepartments());
        }
        axios
            .get(`${baseURL}ticket-embed-trusted-domains`, { headers: authHeaders() })
            .then(({ data }) => setTrustedDomains(Array.isArray(data) ? data : []))
            .catch(() => setTrustedDomains([]));
    }, [dispatch, departments?.length, departmentsLoading]);

    useEffect(() => {
        load(page, perPage, filters, sort);
    }, [page, perPage, filters, sort]);

    const handleSearchDebounced = useCallback((text) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, text: (text ?? "").trim() }));
    }, []);

    const handleFilterChange = (newFilters) => {
        setPage(1);
        setFilters(newFilters);
    };

    const handleSortChange = (column) => {
        setPage(1);
        if (column === "is_active") {
            setSort({
                by: column,
                direction:
                    sort.by === column && sort.direction === "desc" ? "asc" : "desc",
            });
            return;
        }
        setSort({
            by: column,
            direction: sort.by === column && sort.direction === "asc" ? "desc" : "asc",
        });
    };

    const handleColumnVisibilityChange = (column) => {
        const updated = {
            ...columns,
            [column]: { ...columns[column], visible: !columns[column].visible },
        };
        setColumns(updated);
        localStorage.setItem("embedFormsTableColumns", JSON.stringify(updated));
    };

    const departmentOptions = useMemo(
        () => buildDepartmentQuickFilterOptions(departments),
        [departments]
    );
    const trustedDomainOptions = useMemo(
        () => buildTrustedDomainQuickFilterOptions(trustedDomains),
        [trustedDomains]
    );
    const formsQuickFilters = useMemo(
        () => [
            { key: "department", label: "Department", options: departmentOptions },
            { key: "trusted_domain", label: "Trusted domain", options: trustedDomainOptions },
        ],
        [departmentOptions, trustedDomainOptions]
    );

    const handleActiveQuickFilterChange = (isActiveValue) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, is_active: isActiveValue }));
    };

    const visibleColumnCount =
        Object.values(columns).filter((c) => c.visible).length + 1;

    const totalPages = Math.max(1, Math.ceil(pagination.total / perPage));
    const currentPage = pagination.current_page || page;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 16;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = start + maxVisible - 1;
        if (end > totalPages) {
            end = totalPages;
            start = Math.max(1, end - maxVisible + 1);
        }
        if (start > 1) {
            pages.push(
                <Pagination.Item key={1} onClick={() => setPage(1)}>
                    1
                </Pagination.Item>
            );
            if (start > 2) {
                pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }
        for (let i = start; i <= end; i++) {
            pages.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => setPage(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }
        if (end < totalPages) {
            if (end < totalPages - 1) {
                pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            pages.push(
                <Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }
        return pages;
    };

    const handlePaginationChange = (nextPage, nextPerPage) => {
        if (nextPerPage !== perPage) {
            setPerPage(nextPerPage);
            setPage(1);
            return;
        }
        setPage(nextPage);
    };

    const openEmbedCodeModal = (uuid, formName, displayMode = "inline") => {
        setEmbedHowTo({
            show: true,
            uuid: uuid || null,
            formName: formName || "",
            displayMode: displayMode === "floating" ? "floating" : "inline",
        });
    };

    const deleteForm = (id) => {
        confirmDialog({
            title: "Delete embed form?",
            message: "You will not be able to recover it.",
            confirmLabel: "Delete",
            cancelLabel: "Cancel",
            onConfirm: async () => {
                try {
                    await axios.post(
                        `${baseURL}ticket-embed-form-delete/${id}`,
                        {},
                        { headers: authHeaders() }
                    );
                    load();
                } catch (e) {
                    if (handleEmbedAuthRedirect(e)) {
                        return;
                    }
                    alertDialog({
                        title: "Could not delete",
                        message: "The embed form could not be deleted. Please try again.",
                    });
                }
            },
        });
    };

    const setActive = async (id, isActive) => {
        setUpdatingActiveId(id);
        try {
            await axios.post(
                `${baseURL}ticket-embed-form-toggle/${id}`,
                { is_active: isActive },
                { headers: authHeaders() }
            );
            const activeFilter = getEmbedListActiveFilterState(filters.is_active);
            setRows((prev) =>
                patchEmbedListRowActive(prev, id, isActive, activeFilter)
            );
        } catch (e) {
            if (handleEmbedAuthRedirect(e)) {
                return;
            }
            alertDialog({
                title: "Could not update status",
                message: "The form status could not be updated. Please try again.",
            });
        } finally {
            setUpdatingActiveId(null);
        }
    };

    const openPreview = async (id) => {
        try {
            const { data } = await axios.get(
                `${baseURL}ticket-embed-form/${id}/preview-config`,
                { headers: authHeaders() }
            );
            setPreview({ show: true, cfg: data });
        } catch (e) {
            if (handleEmbedAuthRedirect(e)) {
                return;
            }
            alertDialog({
                title: "Could not load preview",
                message: "The preview could not be loaded. Please try again.",
            });
        }
    };

    const renderLoadingSkeleton = () => {
        const skeletonRows = 8;
        return (
            <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                <Table className={tableStyles.table}>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Created</th>
                            <th>Active</th>
                            <th>Department</th>
                            <th>Trusted domains</th>
                            <th
                                className={tableStyles.stickyActions}
                                style={{ width: 110, textAlign: "center" }}
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: skeletonRows }).map((_, idx) => (
                            <tr key={idx}>
                                <td>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={7}
                                            className="mb-1"
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                        <Placeholder
                                            xs={5}
                                            size="sm"
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td className="text-muted">
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={6}
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td className={tableStyles.selectCell}>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={8}
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={7}
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder
                                            xs={10}
                                            style={{ display: "block", borderRadius: 4 }}
                                        />
                                    </Placeholder>
                                </td>
                                <td
                                    className={tableStyles.stickyActions}
                                    style={{ textAlign: "center" }}
                                >
                                    <div style={{ display: "inline-flex", gap: 5 }}>
                                        <Placeholder.Button
                                            variant="secondary"
                                            style={{
                                                width: 26,
                                                height: 26,
                                                padding: 0,
                                                borderRadius: 5,
                                            }}
                                            aria-hidden="true"
                                        />
                                        <Placeholder.Button
                                            variant="secondary"
                                            style={{
                                                width: 26,
                                                height: 26,
                                                padding: 0,
                                                borderRadius: 5,
                                            }}
                                            aria-hidden="true"
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <MainContainer>
            <Container fluid className="embed-forms-page">
                <Row className="pt-4 pb-2">
                    <Col className="table-page-header">
                        <h5 className="flex-grow-1 m-0 text-align-left">Embed ticket forms</h5>
                    </Col>
                </Row>
                <Row className="pb-3">
                    <Col className="d-flex flex-wrap align-items-start gap-2">
                        <TicketsSearch
                            value={filters.text}
                            onDebouncedChange={handleSearchDebounced}
                            isLoading={loading}
                            placeholder="Search forms"
                        />
                        <EmbedListQuickFilters
                            quickFilters={formsQuickFilters}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            isLoading={loading}
                            extraClearKeys={["is_active"]}
                            isExtraFilterActive={(key, f) =>
                                key === "is_active" &&
                                getEmbedListActiveFilterState(f.is_active) !== "all"
                            }
                            afterQuickFilters={
                                <EmbedListActiveQuickFilter
                                    value={filters.is_active}
                                    onChange={handleActiveQuickFilterChange}
                                    isLoading={loading}
                                    itemLabel="forms"
                                />
                            }
                        />
                        <div className="d-flex flex-wrap align-items-start gap-2 tickets-toolbar-actions">
                            <EmbedListLayout
                                columns={columns}
                                onColumnVisibilityChange={handleColumnVisibilityChange}
                            />
                            <EmbedListFilter
                                filterOptions={EMBED_FORMS_ADVANCED_FILTER_OPTIONS}
                                isLoading={loading}
                                filters={filters}
                                onFilterChange={handleFilterChange}
                            />
                            <Button
                                type="button"
                                variant={EmbedFormBtn.secondary}
                                onClick={() => openEmbedCodeModal(null, "")}
                                style={{ whiteSpace: "nowrap" }}
                                disabled={loading}
                            >
                                How to embed
                            </Button>
                            <Button
                                type="button"
                                variant={EmbedFormBtn.primary}
                                onClick={() => history.push("/embed-ticket-forms/new")}
                                style={{ whiteSpace: "nowrap" }}
                                disabled={loading}
                            >
                                <img src={plus} alt="" />{" "}
                                <span className="ms-1">New form</span>
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col className="overflow-x-auto">
                        {loading ? (
                            renderLoadingSkeleton()
                        ) : (
                            <>
                                <div
                                    className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}
                                >
                                    <Table className={tableStyles.table} hover>
                                        <thead>
                                            <tr>
                                                {columns.name.visible && (
                                                    <EmbedListSortableTh
                                                        columnKey="name"
                                                        label={columns.name.label}
                                                        sort={sort}
                                                        onSortChange={handleSortChange}
                                                    />
                                                )}
                                                {columns.created_at.visible && (
                                                    <EmbedListSortableTh
                                                        columnKey="created_at"
                                                        label={columns.created_at.label}
                                                        sort={sort}
                                                        onSortChange={handleSortChange}
                                                    />
                                                )}
                                                {columns.is_active.visible && (
                                                    <EmbedListSortableTh
                                                        columnKey="is_active"
                                                        label={columns.is_active.label}
                                                        sort={sort}
                                                        onSortChange={handleSortChange}
                                                    />
                                                )}
                                                {columns.department.visible && (
                                                    <EmbedListSortableTh
                                                        columnKey="department"
                                                        label={
                                                            <EmbedDepartmentLabelHint id="embed-forms-col-dept" />
                                                        }
                                                        sort={sort}
                                                        onSortChange={handleSortChange}
                                                    />
                                                )}
                                                {columns.trusted_domains.visible && (
                                                    <EmbedListSortableTh
                                                        columnKey="id"
                                                        label={columns.trusted_domains.label}
                                                        sort={sort}
                                                        onSortChange={handleSortChange}
                                                        sortable={false}
                                                    />
                                                )}
                                                <th
                                                    className={tableStyles.stickyActions}
                                                    style={{ width: 110, textAlign: "center" }}
                                                >
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={visibleColumnCount}
                                                        className="text-muted embed-ticket-forms-table__empty"
                                                    >
                                                        No embed forms yet.
                                                    </td>
                                                </tr>
                                            ) : (
                                                rows.map((r) => (
                                            <tr
                                                key={r.id}
                                                className="embed-ticket-forms-table-row"
                                                onClick={() =>
                                                    history.push(
                                                        `/embed-ticket-forms/${r.id}`
                                                    )
                                                }
                                            >
                                                {columns.name.visible && (
                                                <td className="embed-ticket-forms-name-cell">
                                                    {(() => {
                                                        const {
                                                            display,
                                                            full,
                                                            truncated,
                                                        } = formatEmbedFormName(r.name);
                                                        if (!truncated) {
                                                            return (
                                                                <span className={tableStyles.title}>
                                                                    {display}
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip
                                                                        id={`embed-form-name-${r.id}`}
                                                                    >
                                                                        {full}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span
                                                                    className={`${tableStyles.title} embed-ticket-forms-name-cell__text`}
                                                                >
                                                                    {display}
                                                                </span>
                                                            </OverlayTrigger>
                                                        );
                                                    })()}
                                                </td>
                                                )}
                                                {columns.created_at.visible && (
                                                <td className="text-muted">
                                                    {r.created_at || "—"}
                                                </td>
                                                )}
                                                {columns.is_active.visible && (
                                                <td
                                                    className={tableStyles.selectCell}
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <EmbedFormsActiveSelect
                                                        isActive={r.is_active}
                                                        disabled={
                                                            updatingActiveId ===
                                                            r.id
                                                        }
                                                        onChange={(nextActive) =>
                                                            setActive(
                                                                r.id,
                                                                nextActive
                                                            )
                                                        }
                                                    />
                                                </td>
                                                )}
                                                {columns.department.visible && (
                                                <td>
                                                    {r.department ? (
                                                        <ColorBullet
                                                            color={r.department.color}
                                                            text={r.department.name}
                                                        />
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                                )}
                                                {columns.trusted_domains.visible && (
                                                <td className="embed-ticket-forms-trusted-col">
                                                    {(() => {
                                                        const activeList =
                                                            r.trusted_domains ||
                                                            [];
                                                        const inactiveList =
                                                            r.inactive_trusted_domains ||
                                                            [];
                                                        const missingList =
                                                            r.missing_trusted_domains ||
                                                            [];
                                                        const issues = [];
                                                        if (missingList.length > 0) {
                                                            issues.push(
                                                                "Linked trusted domain(s) were deleted from the workspace: " +
                                                                    missingList.join(", ") +
                                                                    ". They’ll be re-linked automatically when added back."
                                                            );
                                                        }
                                                        if (inactiveList.length > 0) {
                                                            issues.push(
                                                                (activeList.length === 0
                                                                    ? "All linked trusted domains are inactive: "
                                                                    : "Some linked trusted domains are inactive: ") +
                                                                    inactiveList.join(", ") +
                                                                    ". The widget won’t load on those sites until they are re-enabled."
                                                            );
                                                        }
                                                        const warningMessage =
                                                            issues.length > 0
                                                                ? issues.join(" ")
                                                                : null;
                                                        const warningLabel =
                                                            missingList.length > 0
                                                                ? "Some trusted domains were deleted"
                                                                : inactiveList.length > 0
                                                                  ? "Some trusted domains are inactive"
                                                                  : null;
                                                        return (
                                                            <span className="embed-ticket-forms-trusted-cell">
                                                                <span className="embed-ticket-forms-trusted-cell__list">
                                                                    {activeList.join(
                                                                        ", "
                                                                    )}
                                                                </span>
                                                                {warningMessage ? (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={
                                                                            <Tooltip
                                                                                id={`embed-trusted-warning-${r.id}`}
                                                                            >
                                                                                {
                                                                                    warningMessage
                                                                                }
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <span
                                                                            className="embed-ticket-forms-trusted-warning"
                                                                            role="img"
                                                                            aria-label={
                                                                                warningLabel
                                                                            }
                                                                            onClick={(
                                                                                e
                                                                            ) =>
                                                                                e.stopPropagation()
                                                                            }
                                                                        >
                                                                            <BsExclamationTriangleFill
                                                                                aria-hidden
                                                                            />
                                                                        </span>
                                                                    </OverlayTrigger>
                                                                ) : null}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                )}
                                                <td
                                                    className={tableStyles.stickyActions}
                                                    style={{ textAlign: "center" }}
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <div style={{ display: "inline-flex", gap: 5 }}>
                                                        <button
                                                            type="button"
                                                            className={tableStyles.actionBtn}
                                                            title="Get embed code"
                                                            aria-label="Get embed code"
                                                            onClick={() =>
                                                                openEmbedCodeModal(
                                                                    r.uuid,
                                                                    r.name,
                                                                    r.display_mode
                                                                )
                                                            }
                                                        >
                                                            <BsClipboard size={11} aria-hidden />
                                                        </button>
                                                        <Dropdown
                                                            align="end"
                                                            onToggle={(nextShow) => {
                                                            if (!nextShow) {
                                                                const el = document.getElementById(
                                                                    `embed-dd-${r.id}`
                                                                );
                                                                if (el) {
                                                                    if (
                                                                        el.__embedMenuRevealRafId !=
                                                                        null
                                                                    ) {
                                                                        cancelAnimationFrame(
                                                                            el.__embedMenuRevealRafId
                                                                        );
                                                                        el.__embedMenuRevealRafId =
                                                                            null;
                                                                    }
                                                                    const menu =
                                                                        el.parentElement?.querySelector(
                                                                            ".dropdown-menu"
                                                                        );
                                                                    menu?.removeAttribute(
                                                                        "data-embed-menu-ready"
                                                                    );
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Dropdown.Toggle
                                                            as="button"
                                                            type="button"
                                                            className={tableStyles.actionBtn}
                                                            id={`embed-dd-${r.id}`}
                                                        >
                                                            <BsThreeDotsVertical size={11} />
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu
                                                            className="embed-ticket-forms-actions-menu"
                                                            popperConfig={
                                                                EMBED_FORMS_ACTIONS_MENU_POPPER
                                                            }
                                                        >
                                                            <Dropdown.Item
                                                                as={Link}
                                                                to={`/embed-ticket-forms/${r.id}`}
                                                            >
                                                                Edit
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                onClick={() =>
                                                                    openPreview(
                                                                        r.id
                                                                    )
                                                                }
                                                            >
                                                                Preview
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                className="text-danger"
                                                                onClick={() =>
                                                                    deleteForm(
                                                                        r.id
                                                                    )
                                                                }
                                                            >
                                                                Delete
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                    </div>
                                                </td>
                                            </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                                <div className={tableStyles.tableFooter}>
                                    <div
                                        className="d-flex align-items-center flex-wrap"
                                        style={{ gap: 10 }}
                                    >
                                        <Pagination className={tableStyles.pagination}>
                                            <Pagination.Prev
                                                disabled={currentPage <= 1}
                                                onClick={() =>
                                                    handlePaginationChange(
                                                        currentPage - 1,
                                                        perPage
                                                    )
                                                }
                                            />
                                            {getPageNumbers()}
                                            <Pagination.Next
                                                disabled={currentPage >= totalPages}
                                                onClick={() =>
                                                    handlePaginationChange(
                                                        currentPage + 1,
                                                        perPage
                                                    )
                                                }
                                            />
                                        </Pagination>
                                        <label
                                            htmlFor="embedFormsPerPageSelect"
                                            className={tableStyles.perPageLabel}
                                        >
                                            Page Size:
                                        </label>
                                        <Form.Control
                                            as="select"
                                            id="embedFormsPerPageSelect"
                                            value={perPage}
                                            onChange={(e) =>
                                                handlePaginationChange(
                                                    1,
                                                    parseInt(e.target.value, 10)
                                                )
                                            }
                                            className={tableStyles.perPage}
                                            style={{ width: 80 }}
                                        >
                                            {[10, 20, 50, 100].map((size) => (
                                                <option key={size} value={size}>
                                                    {size}
                                                </option>
                                            ))}
                                        </Form.Control>
                                        <span className={tableStyles.results}>
                                            Showing {rows.length} of {pagination.total} Forms
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </Container>

            {dialogNodes}
            <EmbedHowToModal
                show={embedHowTo.show}
                onHide={() =>
                    setEmbedHowTo({
                        show: false,
                        uuid: null,
                        formName: "",
                        displayMode: "inline",
                    })
                }
                variant="generic"
                uuid={embedHowTo.uuid}
                formName={embedHowTo.formName}
                displayMode={embedHowTo.displayMode}
            />

            <Modal
                show={preview.show}
                onHide={() => setPreview({ show: false, cfg: null })}
                size="lg"
                centered
                className="embed-forms-ui"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    {preview.cfg ? (
                        <div className="embed-form-editor-preview-panel">
                            <EmbedFormEditorPreview cfg={preview.cfg} />
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        type="button"
                        variant={EmbedFormBtn.primary}
                        onClick={() => setPreview({ show: false, cfg: null })}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </MainContainer>
    );
};

export default EmbedTicketForms;
